/**
 * 空狀態組件庫 | Empty States Component Library
* 提供各種視圖的空狀態顯示
* 注重使用者引導與正面的視覺體驗
 */

import { ReactNode } from 'react';

interface EmptyStateConfig {
  icon: string;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  illustration?: ReactNode;
}

export function EmptyState({ config, size = 'large' }: { config: EmptyStateConfig; size?: 'small' | 'large' }) {
  const sizeClasses = size === 'large'
    ? 'text-6xl mb-4 py-16'
    : 'text-4xl mb-3 py-8';

  return (
    <div className="text-center">
      {config.illustration || (
        <div className={sizeClasses}>
          {config.icon}
        </div>
      )}
      <h3 className={`font-bold text-slate-800 ${size === 'large' ? 'text-xl mb-2' : 'text-lg mb-1'}`}>
        {config.title}
      </h3>
      <p className={`text-slate-500 ${size === 'large' ? 'text-lg mb-1' : 'text-sm mb-3'}`}>
        {config.description}
      </p>
      {config.actionLabel && config.onAction && (
        <button
          onClick={config.onAction}
          className="mt-4 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 active:scale-95"
        >
          {config.actionLabel}
        </button>
      )}
    </div>
  );
}

// 預定義的空狀態配置
export const EmptyStates = {
  noOrders: {
    icon: '📦',
    title: '暫無訂單資料',
    description: '開始建立您的第一個生產訂單，追蹤從投產到出貨的完整流程',
    actionLabel: '➕ 立即新增訂單',
  },
  noWorkOrders: {
    icon: '📋',
    title: '暫無工單資料',
    description: '建立訂單後，系統將自動生成對應的主工單和子工單',
    actionLabel: '前往訂單管理',
  },
  noPersons: {
    icon: '👥',
    title: '尚未加入人員',
    description: '新增人員資料以啟用排程功能，指派工作給團隊成員',
    actionLabel: '➕ 新增人員',
  },
  noInspectionRecords: {
    icon: '📝',
    title: '尚無檢驗記錄',
    description: '建立品質檢驗記錄以追蹤產品品質，確保出貨品質符合標準',
    actionLabel: '📝 新增檢驗記錄',
  },
  noRushOrders: {
    icon: '⚡',
    title: '暫無插單記錄',
    description: '當有緊急訂單需要插單處理時，記錄將顯示於此供您審核',
    actionLabel: '查看訂單列表',
  },
  noBottlenecks: {
    icon: '🎉',
    title: '生產順暢，無瓶頸',
    description: '目前所有資源運作正常，沒有發現明顯的生產瓶頸或風險',
    actionLabel: '查看生產概況',
  },
  noProductionRecords: {
    icon: '📊',
    title: '今日尚無報工',
    description: '完成生產任務後，請填寫報工資料以更新生產進度',
    actionLabel: '查看待報工工單',
  },
  noData: {
    icon: '📭',
    title: '暫無資料',
    description: '相關資料將在您執行操作後自動顯示於此',
  },
  loading: {
    icon: '⏳',
    title: '載入中...',
    description: '正在為您載入最新資料，請稍候片刻',
  },
  success: {
    icon: '✅',
    title: '操作成功',
    description: '您的變更已成功儲存，資料已更新',
  },
  error: {
    icon: '❌',
    title: '發生錯誤',
    description: '系統暫時無法完成您的請求，請稍後再試或聯繫系統管理員',
    actionLabel: '🔄 重新載入',
  },
};

// 載入動畫組件
export function LoadingSpinner({ size = 'medium', message }: { size?: 'small' | 'medium' | 'large'; message?: string }) {
  const sizeClasses = {
    small: 'w-8 h-8 border-3',
    medium: 'w-12 h-12 border-4',
    large: 'w-16 h-16 border-4',
  }[size];

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="relative">
        <div className={`${sizeClasses} border-blue-500/30 rounded-full animate-spin`}></div>
        <div className={`${sizeClasses} border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin absolute top-0`}></div>
      </div>
      {message && (
        <p className="mt-6 text-slate-600 font-medium animate-pulse">{message}</p>
      )}
    </div>
  );
}

// 全屏載入覆蓋層
export function LoadingOverlay({ message = '載入中...', show = true }: { message?: string; show?: boolean }) {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center max-w-sm mx-4">
        <div className="relative mb-6">
          <div className="w-16 h-16 border-4 border-blue-500/30 rounded-full animate-spin"></div>
          <div className="w-16 h-16 border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin absolute top-0"></div>
        </div>
        <p className="text-slate-700 font-semibold text-lg">{message}</p>
        <p className="text-slate-500 text-sm mt-2">請稍候片刻...</p>
      </div>
    </div>
  );
}

//骨架屏組件
export function SkeletonCard() {
  return (
    <div className="bg-white border-2 border-slate-200 rounded-xl p-5 shadow-sm animate-pulse">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-12 h-12 bg-slate-200 rounded-xl"></div>
        <div className="flex-1">
          <div className="h-5 bg-slate-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-slate-200 rounded w-1/2"></div>
        </div>
      </div>
      <div className="space-y-3">
        <div className="h-4 bg-slate-200 rounded"></div>
        <div className="h-4 bg-slate-200 rounded w-3/4"></div>
        <div className="h-4 bg-slate-200 rounded w-1/2"></div>
      </div>
    </div>
  );
}

export function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div className="bg-white border-2 border-slate-200 rounded-xl shadow-lg overflow-hidden">
      <div className="h-16 bg-slate-100 border-b-2 border-slate-200"></div>
      <div className="p-4 space-y-3">
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg animate-pulse">
            <div className="w-24 h-4 bg-slate-200 rounded"></div>
            <div className="flex-1 h-4 bg-slate-200 rounded"></div>
            <div className="w-20 h-4 bg-slate-200 rounded"></div>
            <div className="w-16 h-4 bg-slate-200 rounded"></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// 過渡動畫組件
export function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  return (
    <div
      className="animate-fade-in"
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export function SlideIn({ children, direction = 'up' }: { children: React.ReactNode; direction?: 'up' | 'down' | 'left' | 'right' }) {
  const directionClasses = {
    up: 'animate-slide-up',
    down: 'animate-slide-down',
    left: 'animate-slide-left',
    right: 'animate-slide-right',
  }[direction];

  return <div className={directionClasses}>{children}</div>;
}

// 進度條組件
export function ProgressBar({
  progress,
  color = 'blue',
  size = 'medium',
  showLabel = true
}: {
  progress: number;
  color?: 'blue' | 'green' | 'amber' | 'red' | 'violet';
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}) {
  const colorClasses = {
    blue: 'from-blue-500 to-cyan-400',
    green: 'from-green-500 to-emerald-400',
    amber: 'from-amber-500 to-orange-400',
    red: 'from-red-500 to-rose-400',
    violet: 'from-violet-500 to-purple-400',
  }[color];

  const sizeClasses = {
    small: 'h-1.5',
    medium: 'h-2.5',
    large: 'h-3',
  }[size];

  return (
    <div className="flex items-center gap-3">
      <div className={`flex-1 ${sizeClasses} bg-slate-100 rounded-full overflow-hidden shadow-inner`}>
        <div
          className={`h-full bg-gradient-to-r ${colorClasses} transition-all duration-500`}
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>
      {showLabel && (
        <span className="text-sm font-bold text-slate-700 min-w-[3rem]">{progress}%</span>
      )}
    </div>
  );
}

// 狀態徽章組件
export function StatusBadge({
  status,
  size = 'medium',
  icon = true
}: {
  status: 'pending' | 'in-progress' | 'completed' | 'delayed' | 'warning' | 'error';
  size?: 'small' | 'medium' | 'large';
  icon?: boolean;
}) {
  const config = {
    'pending': {
      label: '待處理',
      bg: 'bg-gradient-to-r from-slate-100 to-zinc-100',
      text: 'text-slate-700',
      border: 'border-slate-300',
      dot: 'bg-slate-500',
      icon: '⏳',
    },
    'in-progress': {
      label: '進行中',
      bg: 'bg-gradient-to-r from-amber-100 to-orange-100',
      text: 'text-amber-700',
      border: 'border-amber-300',
      dot: 'bg-amber-500',
      icon: '⚡',
    },
    'completed': {
      label: '已完成',
      bg: 'bg-gradient-to-r from-green-100 to-emerald-100',
      text: 'text-green-700',
      border: 'border-green-300',
      dot: 'bg-green-500',
      icon: '✓',
    },
    'delayed': {
      label: '已延遲',
      bg: 'bg-gradient-to-r from-red-100 to-rose-100',
      text: 'text-red-700',
      border: 'border-red-300',
      dot: 'bg-red-500',
      icon: '⚠️',
    },
    'warning': {
      label: '警告',
      bg: 'bg-gradient-to-r from-amber-100 to-yellow-100',
      text: 'text-amber-700',
      border: 'border-amber-300',
      dot: 'bg-amber-500',
      icon: '⚠️',
    },
    'error': {
      label: '錯誤',
      bg: 'bg-gradient-to-r from-red-100 to-rose-100',
      text: 'text-red-700',
      border: 'border-red-300',
      dot: 'bg-red-500',
      icon: '✕',
    },
  }[status];

  const sizeClasses = {
    small: 'px-2.5 py-1 text-xs',
    medium: 'px-3.5 py-1.5 text-sm',
    large: 'px-4 py-2 text-base',
  }[size];

  return (
    <span className={`inline-flex items-center gap-2 rounded-full font-semibold shadow-sm border ${config.bg} ${config.text} ${config.border} ${sizeClasses}`}>
      {icon && (
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
      )}
      <span>{config.icon}</span>
      <span>{config.label}</span>
    </span>
  );
}

/**
 * 搜尋無結果空狀態
 */
interface SearchEmptyProps {
  query?: string;
  onClear?: () => void;
  type?: 'orders' | 'persons' | 'machines' | 'general';
}

export function SearchEmpty({ query = '', onClear, type = 'general' }: SearchEmptyProps) {
  const typeMessages = {
    orders: {
      icon: '📦',
      title: '找不到符合的訂單',
      description: `沒有找到包含 "${query}" 的訂單，請嘗試其他關鍵字`,
    },
    persons: {
      icon: '👥',
      title: '找不到符合的人員',
      description: `沒有找到包含 "${query}" 的人員，請嘗試其他關鍵字`,
    },
    machines: {
      icon: '🔧',
      title: '找不到符合的機台',
      description: `沒有找到包含 "${query}" 的機台，請嘗試其他關鍵字`,
    },
    general: {
      icon: '🔍',
      title: '找不到相符結果',
      description: `沒有找到包含 "${query}" 的內容，請嘗試其他關鍵字`,
    },
  };

  const config = typeMessages[type];

  return (
    <div className="text-center py-16 px-4">
      <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-slate-50 to-slate-100 rounded-full mb-6 animate-float">
        <span className="text-5xl">{config.icon}</span>
      </div>
      <h3 className="text-xl font-bold text-slate-800 mb-2">{config.title}</h3>
      <p className="text-slate-500 mb-6 max-w-md mx-auto">{config.description}</p>
      {onClear && (
        <button
          onClick={onClear}
          className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 active:scale-95"
        >
          清除搜尋條件
        </button>
      )}
    </div>
  );
}

/**
 * 錯誤邊界空狀態 - 用於錯誤發生時
 */
interface ErrorBoundaryProps {
  error?: Error;
  resetError?: () => void;
  componentName?: string;
}

export function ErrorBoundaryFallback({ error, resetError, componentName = '元件' }: ErrorBoundaryProps) {
  return (
    <div className="min-h-[400px] flex items-center justify-center p-4">
      <div className="text-center max-w-lg">
        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-red-50 to-rose-50 rounded-full mb-6">
          <span className="text-5xl">⚠️</span>
        </div>

        <h2 className="text-2xl font-bold text-slate-800 mb-3">
          {componentName} 發生錯誤
        </h2>

        <p className="text-slate-500 mb-6">
          {error?.message || '發生未預期的錯誤，請重新整理頁面或聯繫系統管理員'}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 text-slate-700 rounded-lg transition-all text-sm font-semibold shadow-sm active:scale-95"
          >
            🔄 重新整理頁面
          </button>
          {resetError && (
            <button
              onClick={resetError}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 active:scale-95"
            >
              🔙 返回上一頁
            </button>
          )}
        </div>

        {import.meta.env.DEV && error && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm font-medium text-slate-600 hover:text-slate-800 mb-2">
              錯誤詳情（開發模式）
            </summary>
            <pre className="mt-2 p-3 bg-red-50 rounded-lg text-xs text-red-700 overflow-auto max-h-40">
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

/**
 * 網路錯誤空狀態
 */
export function NetworkError({ onRetry }: { onRetry?: () => void }) {
  return (
    <div className="text-center py-16 px-4">
      <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-amber-50 to-orange-50 rounded-full mb-6">
        <span className="text-5xl">🌐</span>
      </div>

      <h2 className="text-2xl font-bold text-slate-800 mb-3">網路連線失敗</h2>

      <p className="text-slate-500 mb-6 max-w-md mx-auto">
        無法連線到伺服器，請檢查您的網路連線後重試
      </p>

      {onRetry && (
        <button
          onClick={onRetry}
          className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 active:scale-95"
        >
          🔄 重新連線
        </button>
      )}
    </div>
  );
}

/**
 * 權限不足空狀態
 */
export function PermissionDenied({ action = '執行此操作' }: { action?: string }) {
  return (
    <div className="text-center py-16 px-4">
      <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-violet-50 to-purple-50 rounded-full mb-6">
        <span className="text-5xl">🔒</span>
      </div>

      <h2 className="text-2xl font-bold text-slate-800 mb-3">權限不足</h2>

      <p className="text-slate-500 mb-6 max-w-md mx-auto">
        您沒有權限{action}，請聯繫系統管理員以獲取相關權限
      </p>

      <button
        onClick={() => window.history.back()}
        className="px-6 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 text-slate-700 rounded-lg transition-all text-sm font-semibold shadow-sm active:scale-95"
      >
        🔙 返回上一頁
      </button>
    </div>
  );
}

/**
 * 未授權空狀態
 */
export function Unauthorized() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4">
      <div className="text-center max-w-lg">
        <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-red-100 to-rose-100 rounded-full mb-8 shadow-lg">
          <span className="text-6xl">🔐</span>
        </div>

        <h1 className="text-4xl font-bold text-slate-800 mb-4">未授權存取</h1>

        <p className="text-lg text-slate-500 mb-8">
          您的存取權杖已過期或無效，請重新登入以繼續使用系統
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => window.location.href = '/login'}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-lg transition-all text-base font-semibold shadow-lg shadow-blue-500/30 active:scale-95"
          >
            重新登入
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="px-8 py-3 bg-gradient-to-r from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 text-slate-700 rounded-lg transition-all text-base font-semibold shadow-sm active:scale-95"
          >
            返回首頁
          </button>
        </div>
      </div>
    </div>
  );
}
